import * as React from 'react';

import { useReviewsSelection } from '../context';
import type { AcceptAndPublishVisibility } from '../context';
import { SuggestionsHeaderTitleNav } from './suggestions-header-title-nav';
import { SuggestionsHeaderToolbar, type SuggestionHeaderAction } from './suggestions-header-toolbar';

import { cn } from '@/lib/utils';

export interface SuggestionsHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  isNew: boolean;
  isAccepted?: boolean;
  suggestionsCount: number;
  sourceIcon: React.ReactNode;
  sourceDescription: string | null;
  currentArticleIndex: number;
  totalArticles: number;
  displayTotalArticles?: number;
  showActionButtons?: boolean;
  onNextArticle: () => void;
  onPreviousArticle: () => void;
}

export const REASON_TITLE = 'Why is Brainfish suggesting this?';

export const SuggestionsHeader = React.forwardRef<HTMLDivElement, SuggestionsHeaderProps>(function SuggestionsHeader(
  {
    title,
    isNew,
    isAccepted = false,
    suggestionsCount,
    sourceIcon,
    sourceDescription,
    currentArticleIndex,
    totalArticles,
    displayTotalArticles,
    showActionButtons = true,
    onNextArticle,
    onPreviousArticle,
    className,
    ...props
  },
  ref,
) {
  const {
    onApproveAllSuggestions,
    onRejectAllSuggestions,
    onAcceptAndPublish,
    onApplyAllInEditor,
    onRejectAllInEditor,
    onOpenArticleLink,
    onSourceLinkClick,
    breadcrumb,
    suggestionReasonBody,
    hideAcceptAndPublishPublic = false,
  } = useReviewsSelection();

  const [pendingAction, setPendingAction] = React.useState<SuggestionHeaderAction | null>(null);
  const pendingActionRef = React.useRef<SuggestionHeaderAction | null>(null);

  const runAction = React.useCallback(async (action: SuggestionHeaderAction, fn: () => void | Promise<void>) => {
    if (pendingActionRef.current) return;

    pendingActionRef.current = action;
    setPendingAction(action);
    try {
      await fn();
    } finally {
      pendingActionRef.current = null;
      setPendingAction(null);
    }
  }, []);

  const handleRejectAll = React.useCallback(async () => {
    await runAction('reject', async () => {
      if (onRejectAllInEditor) await onRejectAllInEditor();
      await Promise.resolve(onRejectAllSuggestions());
    });
  }, [onRejectAllInEditor, onRejectAllSuggestions, runAction]);

  const handleAcceptAll = React.useCallback(async () => {
    await runAction('accept-draft', async () => {
      if (onApplyAllInEditor) await onApplyAllInEditor();
      await Promise.resolve(onApproveAllSuggestions());
    });
  }, [onApplyAllInEditor, onApproveAllSuggestions, runAction]);

  const handleAcceptAndPublish = React.useCallback(
    async (visibility: AcceptAndPublishVisibility) => {
      if (!onAcceptAndPublish) return;

      const action: SuggestionHeaderAction =
        visibility === 'public' ? 'accept-publish-public' : 'accept-publish-internal';

      await runAction(action, async () => {
        if (onApplyAllInEditor) await onApplyAllInEditor();
        await Promise.resolve(onAcceptAndPublish(visibility));
      });
    },
    [onApplyAllInEditor, onAcceptAndPublish, runAction],
  );

  return (
    <div ref={ref} className={cn('flex flex-col gap-4', className)} {...props}>
      <SuggestionsHeaderTitleNav
        title={title}
        breadcrumb={breadcrumb}
        onOpenArticleLink={onOpenArticleLink}
        currentArticleIndex={currentArticleIndex}
        totalArticles={totalArticles}
        displayTotalArticles={displayTotalArticles}
        onNextArticle={onNextArticle}
        onPreviousArticle={onPreviousArticle}
      />
      <SuggestionsHeaderToolbar
        isNew={isNew}
        isAccepted={isAccepted}
        suggestionsCount={suggestionsCount}
        sourceIcon={sourceIcon}
        sourceDescription={sourceDescription}
        onSourceLinkClick={onSourceLinkClick}
        showActionButtons={showActionButtons}
        suggestionReasonBody={suggestionReasonBody}
        hideAcceptAndPublishPublic={hideAcceptAndPublishPublic}
        pendingAction={pendingAction}
        onAcceptAndPublish={onAcceptAndPublish}
        onRejectAll={handleRejectAll}
        onAcceptAll={handleAcceptAll}
        onAcceptAndPublishAction={handleAcceptAndPublish}
      />
    </div>
  );
});
